import React, { useMemo } from "react";
import { Calendar } from "lucide-react";
import { useGetMyLeaveBalanceQuery } from "../../store/apis/leave.api";
import { useGetMasterConfigByCategoryQuery } from "../../store/apis/masterConfig.api";
import { CARD_CLASS } from "../../constants/dashboard";
import { ShimmerBlock } from "../common/Skeleton";

const LEAVE_BAR_COLORS = ["#6D28D9", "#059669", "#2563EB"];

const LeaveSummaryCard: React.FC = () => {
  const { data: balanceData, isLoading: loadingBalance } =
    useGetMyLeaveBalanceQuery();
  const { data: tenantLeaveConfigs = [], isLoading: loadingConfigs } =
    useGetMasterConfigByCategoryQuery("leave_type");

  const leaveTypes = useMemo(() => {
    const rawBalances = balanceData?.types || [];

    // Sort existing assigned balances:
    // 1. Highest available balance first
    // 2. Then highest credited allocation
    const sortedBalances = [...rawBalances].sort((a, b) => {
      const aAvail = Number(a.available || 0);
      const bAvail = Number(b.available || 0);
      if (bAvail !== aAvail) return bAvail - aAvail;
      return Number(b.credited || 0) - Number(a.credited || 0);
    });

    // If user already has 3 or more leave types with balance records, display top 3
    if (sortedBalances.length >= 3) {
      return sortedBalances.slice(0, 3);
    }

    // Set of already-included leave identifiers (by code or label)
    const existingKeys = new Set(
      sortedBalances.map((t) => (t.code || t.label || "").toLowerCase().replace(/[^a-z0-9]/g, ""))
    );

    // Dynamic tenant fallback: Pull from tenant's configured Master Config for 'leave_type'
    const tenantFallbacks = (tenantLeaveConfigs || [])
      .filter((cfg: any) => {
        const key = (cfg.filterCode || cfg.displayName || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        return key && !existingKeys.has(key);
      })
      .map((cfg: any) => ({
        id: cfg._id || cfg.id,
        code: cfg.filterCode || cfg.code,
        label: cfg.displayName || cfg.label || "Leave",
        available: 0,
        credited: 0,
        used: 0,
      }));

    const needed = 3 - sortedBalances.length;
    const fillers = tenantFallbacks.slice(0, needed);

    return [...sortedBalances, ...fillers];
  }, [balanceData, tenantLeaveConfigs]);

  const loading = loadingBalance || loadingConfigs;

  if (loading) {
    return (
      <div className={`${CARD_CLASS} p-4 sm:p-5 h-full min-h-[200px] flex flex-col`}>
        <div className="flex items-center gap-2 mb-4 shrink-0">
          <ShimmerBlock className="h-[18px] w-[18px] rounded-md" />
          <ShimmerBlock className="h-4 w-36" />
        </div>
        <div className="flex-1 flex flex-col justify-center gap-3.5">
          {[1, 2, 3].map((i) => (
            <div key={i}>
              <div className="flex items-center justify-between mb-2">
                <ShimmerBlock className="h-3.5 w-20" />
                <ShimmerBlock className="h-3.5 w-16" />
              </div>
              <ShimmerBlock className="h-2 w-full rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`${CARD_CLASS} p-4 sm:p-5 h-auto lg:h-full min-h-[170px] lg:min-h-[200px] flex flex-col`}>
      <h3 className="m-0 mb-3 text-heading-sm font-bold text-text-primary flex items-center gap-2 shrink-0 tracking-tight">
        <Calendar className="w-[18px] h-[18px] text-primary-600" />
        My Leave Balance
      </h3>

      <div className="flex-1 min-h-0 max-h-[220px] lg:max-h-none overflow-y-auto scrollbar-thin scrollbar-thumb-border-input pr-1">
        {leaveTypes.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center py-4 text-center">
            <div className="w-12 h-12 bg-layout-hover rounded-full flex items-center justify-center mb-3">
              <Calendar className="w-5 h-5 text-text-tertiary" />
            </div>
            <p className="text-body-sm font-semibold text-text-secondary">
              No leave balance available
            </p>
            <p className="text-caption text-text-tertiary mt-1">
              Your leave balance will appear here
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3.5">
            {leaveTypes.map((type, index) => {
              const credited = Number(type.credited || 0);
              const available = Number(type.available || 0);
              const fill = credited > 0 ? Math.min((available / credited) * 100, 100) : 0;
              const color = LEAVE_BAR_COLORS[index % LEAVE_BAR_COLORS.length];

              return (
                <div key={type.code || type.id || index}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-body-sm font-semibold text-text-strong truncate">
                      {type.label}
                    </span>
                    <span className="text-caption font-bold text-text-primary flex-none">
                      {credited > 0 ? `${available} / ${credited} Days` : `${available} Days`}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-track overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{
                        width: `${fill}%`,
                        background: fill > 0 ? color : "var(--color-border-secondary)",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};


export default LeaveSummaryCard;
